import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AppDialogComponent } from 'src/app/components/app-dialog/app-dialog.component';
import { Device, DeviceAccessSource } from 'src/app/models/data/device';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceGroup } from 'src/app/models/data/device';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { Mode } from 'src/app/models/model';
import { AuthService, UserSession } from 'src/app/services/auth.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { AUDIT_LOG_COLUMN_DEVICE_GROUP } from 'src/app/app.constants';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { DeviceGroupDevicesComponent } from '../device-group-devices/device-group-devices.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { UserAccessTabComponent } from '../../shared/user-access-tab/user-access-tab.component';
import { CompanyAccessTabComponent } from '../../shared/company-access/company-access.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';

@Component({
	selector: 'app-device-group-main-tab',
	templateUrl: './device-group-main-tab.component.html',
	styleUrls: ['./device-group-main-tab.component.scss'],
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		DeviceGroupDevicesComponent,
		BreadcrumbLabelDirective,
		UserAccessTabComponent,
		CompanyAccessTabComponent,
		AuditLogListComponent,
	],
})
export class DeviceGroupMainTabComponent implements OnInit, OnDestroy {
	mode: Mode;
	userSession?: UserSession;
	userSubscription?: Subscription;
	deviceGroup?: DeviceGroup;
	companyId?: string;
	hasUserRoleManagement: boolean = false;
	hasDeviceManagement: boolean = false;
	canAccessAuditLogs: boolean = false;
	hiddenAuditLogColumns = [AUDIT_LOG_COLUMN_DEVICE_GROUP];

	deviceFilterParams: QueryParam[] = [];
	deviceGroupId = '';
	public devices: {
		device: Device;
		groupNames?: string[];
		source?: DeviceAccessSource;
	}[] = [];

	filter: QueryParam[] = [];

	deviceGroupDevicesBreadcrumbLabel = '';
	deviceGroupUsersBreadcrumbLabel = '';
	deviceGroupCompaniesBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';

	hasAjaxDevice = false;
	hasOnlyPDQ = false;

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly authService: AuthService,
		private readonly scService: SystemCapabilityService,
		private dialog: MatDialog
	) {
		this.mode =
			this.router.lastSuccessfulNavigation?.extras?.state?.mode ??
			Mode.viewOnly;
		this.userSubscription = this.authService
			.userSession()
			.subscribe(async (session) => {
				this.userSession = session;
				this.companyId =
					this.userSession?.assignedRole?.entityRelationship?.parentEntityId;
				await this.populateDeviceList();
			});
		this.hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
		this.hasDeviceManagement = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
		this.canAccessAuditLogs = this.scService.has(
			SystemCapabilityEnum.auditLogs
		);
	}

	async ngOnInit(): Promise<void> {
		await this.loadFromParams();
		this.updateDeviceGroupLabels();
	}

	ngOnDestroy(): void {
		this.userSubscription?.unsubscribe();
		this.userSubscription = undefined;
	}

	async populateDeviceList(): Promise<void> {
		// Must have CompanyId to populate the list
		if (!this.companyId) {
			console.error('No company id...');
			this.dialog.open(AppDialogComponent, {
				data: {
					title: 'Error',
					message: 'Company ID is missing, unable to load device list.',
				},
			});
			return;
		}

		// we only want to return devices that are still in the Device Group
		this.filter.push(
			{ param: 'deviceGroupDeviceStatus', value: ['active', 'upcoming'] },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 5000 } // arbitrary number of devices
		);
		this.filter.push({
			param: 'deviceGroupDeviceStatus',
			value: ['active', 'upcoming'],
		});
		const hasRowIndex = this.filter.some((f) => f.param === 'rowIndex');
		const hasRowCount = this.filter.some((f) => f.param === 'rowCount');
		if (!hasRowIndex) {
			this.filter.push({ param: 'rowIndex', value: 0 });
		}
		if (!hasRowCount) {
			this.filter.push({ param: 'rowCount', value: 31 });
		}
		const response = await this.apiService.getDevicesByDeviceGroupId(
			this.deviceGroupId,
			this.filter
		);
		this.devices = response.data;
		this.hasAjaxDevice = !!response.hasAjaxDevice;
		this.hasOnlyPDQ = !!response.hasOnlyPDQ;
	}

	get deviceGroupName(): string {
		return this.deviceGroup?.name ?? '';
	}

	async loadFromParams(): Promise<void> {
		this.deviceGroupId = this.route.snapshot.params.id;
		await this.apiService
			.getDeviceGroupById(this.deviceGroupId)
			.then((group) => (this.deviceGroup = group));
		this.deviceFilterParams.push({
			param: 'deviceGroupId',
			value: this.deviceGroupId,
		});
	}

	updateDeviceGroupLabels(): void {
		this.deviceGroupDevicesBreadcrumbLabel = `${this.deviceGroup?.name}`;
		this.deviceGroupUsersBreadcrumbLabel = `${this.deviceGroup?.name}`;
		this.deviceGroupCompaniesBreadcrumbLabel = `${this.deviceGroup?.name}`;
		this.auditLogBreadcrumbLabel = `${this.deviceGroup?.name}`;
	}

	// DE4763: if the user has the "Device Management",
	// they should be able to view both the user and the company access tab.
	canViewCompanyOrUserAccessTabs() {
		return this.hasDeviceManagement;
	}
}
